import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from "@angular/common/http";
import {
    ApplicationConfig,
    importProvidersFrom,
    isDevMode,
    LOCALE_ID,
    provideZoneChangeDetection
} from "@angular/core";

import { BrowserModule } from "@angular/platform-browser";
import { provideAnimations } from "@angular/platform-browser/animations";
import { provideRouter, withDisabledInitialNavigation, withEnabledBlockingInitialNavigation } from "@angular/router";
import {
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MSAL_INTERCEPTOR_CONFIG,
    MsalBroadcastService,
    MsalGuard,
    MsalGuardConfiguration,
    MsalInterceptor,
    MsalInterceptorConfiguration,
    MsalService
} from "@azure/msal-angular";
import {
    BrowserCacheLocation,
    BrowserUtils,
    InteractionType,
    IPublicClientApplication,
    LogLevel,
    PublicClientApplication
} from "@azure/msal-browser";
import { provideTransloco } from "@jsverse/transloco";
import { ConfirmationService, MessageService } from "primeng/api";
import { MessageModule } from "primeng/message";
import { ToastModule } from "primeng/toast";

// animations are provided via provideAnimations() below
import { LoadingInterceptor } from "@components/loading/loading.interceptor";
import { AzureAdService } from "@core/services/azure-ad.service";
import { LoadingService } from "@core/services/loading.service";
import { LocaleService } from "@core/services/locale.service";
import { NotificationService } from "@core/services/notification.service";
import { SessionTimeoutInterceptor } from "@core/services/session-timeout.interceptor";
import { TimeoutInterceptor } from "@core/services/timeout.interceptor";
import Aura from "@primeuix/themes/aura";
import { environment } from "environments/environments";
import { providePrimeNG } from "primeng/config";
import { routes } from "./app.routes";
import { TranslocoHttpLoader } from "./core/loaders";

export const initialNavigation =
    !BrowserUtils.isInIframe() && !BrowserUtils.isInPopup()
        ? withEnabledBlockingInitialNavigation() // Set to enabledBlocking to use Angular Universal
        : withDisabledInitialNavigation();

const AVAILABLE_LANGS = ["pt-BR", "en-US", "es-ES"];
const DEFAULT_LANG = "en-US";

/**
 * Lê o idioma persistido no localStorage de forma resiliente.
 * O LocalStorageService grava com JSON.stringify, então o valor cru pode vir
 * envolto em aspas (ex.: "es-ES"). Aqui desserializamos e validamos contra os
 * idiomas suportados para evitar que o Transloco tente carregar ./i18n/"es-ES".json (404).
 */
function getPersistedLang(): string {
    const raw = localStorage.getItem("lang");
    if (!raw) {
        return DEFAULT_LANG;
    }

    let lang = raw;
    try {
        const parsed = JSON.parse(raw);
        if (typeof parsed === "string") {
            lang = parsed;
        }
    } catch {
        // valor não é JSON; usa como está
    }

    return AVAILABLE_LANGS.includes(lang) ? lang : DEFAULT_LANG;
}

export const appConfig: ApplicationConfig = {
    providers: [
        providePrimeNG({
            theme: {
                preset: Aura,
                options: {
                    darkModeSelector: ".app-dark"
                }
            }
        }),
        provideTransloco({
            config: {
                availableLangs: AVAILABLE_LANGS,
                defaultLang: getPersistedLang(),
                reRenderOnLangChange: true,
                prodMode: !isDevMode(),
                missingHandler: {
                    logMissingKey: false
                }
            },
            loader: TranslocoHttpLoader
        }),
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideRouter(routes, initialNavigation),
        importProvidersFrom(BrowserModule, ToastModule, MessageModule),
        provideAnimations(),
        provideHttpClient(withInterceptorsFromDi(), withFetch()),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true
        },
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: SessionTimeoutInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TimeoutInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: LoadingInterceptor,
            multi: true
        },
        {
            provide: LOCALE_ID,
            useFactory: (localeService: LocaleService) => localeService.locale,
            deps: [LocaleService]
        },
        { provide: "Window", useValue: window },
        { provide: "BASE_URL", useFactory: getBaseUrl, deps: [] },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        LoadingService,
        AzureAdService,
        MessageService,
        ConfirmationService,
        NotificationService
    ]
};

export function loggerCallback(logLevel: LogLevel, message: string) {
    console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
    return new PublicClientApplication({
        auth: {
            clientId: environment.Client_id_azure,
            authority: environment.Authority,
            redirectUri: environment.RedirectUri,
            postLogoutRedirectUri: environment.RedirectUri,
            navigateToLoginRequestUrl: true
        },
        cache: {
            cacheLocation: BrowserCacheLocation.SessionStorage,
            // storeAuthStateInCookie mantido em false: o fluxo é InteractionType.Redirect (página
            // inteira, sem iframe/popup), então o cookie de estado do MSAL é dispensável. Habilitá-lo
            // cria um cookie de auth que não pode ser HttpOnly por design (MSAL o lê via JS), gerando
            // falso-positivo em DAST ("Session Cookie sem HttpOnly"). Só reativar se houver necessidade
            // de suporte a iframe/Safari ITP.
            storeAuthStateInCookie: false
        },
        system: {
            allowNativeBroker: false
            // loggerOptions: {
            //     loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
            //         if (containsPii) {
            //             return;
            //         }
            //         switch (level) {
            //             case LogLevel.Error:
            //                 console.error(message);
            //                 break;
            //             case LogLevel.Warning:
            //                 console.warn(message);
            //                 break;
            //             default:
            //                 console.log(message);
            //         }
            //     },
            //     piiLoggingEnabled: false
            // }
        }
    });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
    const protectedResourceMap = new Map<string, Array<string> | null>();
    protectedResourceMap.set(environment.Graph_endpoint, ["User.Read", "Mail.Send", "email", "openid", "profile"]);
    protectedResourceMap.set(environment.BaseUrl + "report-control-preview/", null);
    protectedResourceMap.set(environment.BaseUrl + "upload-request-documents/", null);
    protectedResourceMap.set(environment.BaseUrl + "entity-response-letters/", null);
    protectedResourceMap.set(environment.BaseUrl + "public/report-working-paper/", null);
    protectedResourceMap.set(environment.BaseUrl, environment.scopeUrl);

    return {
        interactionType: InteractionType.Redirect,
        protectedResourceMap
    };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
    return {
        interactionType: InteractionType.Redirect,
        authRequest: {
            scopes: [...environment.scopeUrl, "Mail.Send", "email", "openid", "profile"]
        },
        loginFailedRoute: "/login-failed"
    };
}

export function getBaseUrl() {
    return document.getElementsByTagName("base")[0].href;
}
